import React, { useEffect, useState } from "react";
import api from "../lib/api";

// --- Helpers ---
function fmtIso(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toISOString().slice(0, 10);
  } catch {
    return s;
  }
}

function Stat({ label, value, accent }) {
  return (
    <div className="surface-1 p-5" data-testid={`vdash-stat-${label.replace(/\s+/g, "-")}`}>
      <p className="label-eyebrow mb-1">{label}</p>
      <p
        className="font-display text-2xl font-light"
        style={{ color: accent ? "var(--highlight)" : undefined }}
      >
        {value ?? "—"}
      </p>
    </div>
  );
}

function Section({ title, children, testid, count }) {
  return (
    <div className="bento-card p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="label-eyebrow">{title}</p>
        {count != null && (
          <p className="text-[10px] uppercase tracking-widest text-neutral-600">
            {count} {count === 1 ? "row" : "rows"}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function Bar({ value, max, accent }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
      <div
        className="h-full"
        style={{
          width: `${pct}%`,
          background: accent || "var(--highlight)",
          transition: "width 400ms ease",
        }}
      />
    </div>
  );
}

// --- Sub-sections ---
function Overview({ data }) {
  if (!data) return null;
  const success = data.successful_reviews || 0;
  const total = data.total_uploads || 0;
  const successRate = total > 0 ? Math.round((success / total) * 100) : 0;
  return (
    <div data-testid="vdash-overview-section">
      <p className="label-eyebrow mb-3">overview · last {data.since_days}d</p>
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-px mb-2"
        style={{ background: "var(--border-default)" }}
      >
        <Stat label="total uploads" value={total} />
        <Stat label="unique uploaders" value={data.unique_users} />
        <Stat label="bowling" value={data.bowling_videos} />
        <Stat label="batting" value={data.batting_videos} />
        <Stat label="successful" value={success} accent />
        <Stat label="rejected" value={data.rejected_reviews} />
        <Stat label="failed" value={data.failed_reviews} />
        <Stat label="avg / user" value={data.avg_videos_per_user} />
      </div>
      <p className="text-[11px] text-neutral-600 mt-2">
        Success rate: <span className="text-neutral-300">{successRate}%</span>
      </p>
    </div>
  );
}

// --- Iter 41: ffmpeg health banner ---
// Hard rule from founder: if ffmpeg is missing, this banner MUST be impossible
// to miss. Red when status='missing', amber when status='degraded' (ffmpeg
// present but ffprobe absent → normalization works but slower).
function FfmpegHealthBanner({ data }) {
  if (!data) return null;
  if (data.status === "ok") return null;

  const isMissing = data.status === "missing";
  return (
    <div
      data-testid="ffmpeg-health-banner"
      data-status={data.status}
      className={`rounded-md border p-4 mb-4 ${
        isMissing
          ? "border-red-600 bg-red-950/40"
          : "border-amber-600 bg-amber-950/30"
      }`}
    >
      <p
        className={`text-sm font-semibold ${
          isMissing ? "text-red-300" : "text-amber-300"
        }`}
        data-testid="ffmpeg-health-headline"
      >
        {isMissing
          ? "Video normalization is NOT active — ffmpeg missing."
          : "Video normalization is running in DEGRADED mode — ffprobe missing."}
      </p>
      <p
        className={`text-xs mt-1 ${
          isMissing ? "text-red-200/80" : "text-amber-200/80"
        }`}
      >
        {isMissing
          ? "Every upload is passing through to Gemini un-normalized. Expect ~63% rejection rate to persist. Add imageio-ffmpeg to backend/requirements.txt or install system ffmpeg in the deploy image."
          : "ffmpeg is available but ffprobe is not — the 'skip-canonical' optimization is disabled and every upload will be transcoded. Normalization still works; this is a performance warning."}
      </p>
      <div
        className="text-[10px] mt-2 text-neutral-400 tabular-nums grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1"
        data-testid="ffmpeg-health-details"
      >
        <span>source: <span className="text-neutral-200">{data.ffmpeg_source}</span></span>
        <span>version: <span className="text-neutral-200">{data.ffmpeg_version || "—"}</span></span>
        <span>ffmpeg: <span className="text-neutral-200">{data.ffmpeg_path || "missing"}</span></span>
        <span>ffprobe: <span className="text-neutral-200">{data.ffprobe_path || "missing"}</span></span>
      </div>
    </div>
  );
}

// Iter 42.4 — Browser breakdown card. Shows uploads, success rate, and
// share of rejections by browser tag (chrome / safari / inapp_instagram /
// inapp_facebook / etc.). Surfaces Instagram WebView as a failure source.
function BrowserBreakdown({ data }) {
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return null;
  const BROWSER_LABELS = {
    chrome:           "Chrome",
    safari:           "Safari",
    edge:             "Edge",
    firefox:          "Firefox",
    inapp_instagram:  "Instagram in-app",
    inapp_facebook:   "Facebook in-app",
    inapp_twitter:    "Twitter/X in-app",
    inapp_line:       "LINE in-app",
    inapp_snapchat:   "Snapchat in-app",
    inapp_tiktok:     "TikTok in-app",
    inapp_android_webview: "Android WebView",
    android_webview:  "Android WebView",
    other:            "Other",
    unknown:          "Unknown",
  };
  const inAppRow = data.rows.find((r) => r.browser?.startsWith("inapp_"));
  return (
    <div className="bento-card p-6" data-testid="vdash-browser-breakdown">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <p className="label-eyebrow">uploads by browser</p>
        <p className="text-[10px] uppercase tracking-widest text-neutral-600">
          last {data.since_days}d · {data.total_uploads} uploads · {data.total_rejected} rejected
        </p>
      </div>
      {inAppRow && inAppRow.rejection_share_pct >= 10 && (
        <div
          className="rounded border border-amber-900/50 bg-amber-950/20 p-3 mb-3 text-xs text-amber-200"
          data-testid="vdash-browser-inapp-warning"
        >
          <span className="text-amber-300 font-medium">Heads up:</span>
          {" "}{BROWSER_LABELS[inAppRow.browser] || inAppRow.browser} accounts for
          {" "}{inAppRow.rejection_share_pct}% of all upload rejections in this window.
          {" "}The Iter 42.4 in-app banner advises these users to switch to Chrome/Safari.
        </div>
      )}
      <div className="space-y-1.5" data-testid="vdash-browser-rows">
        {data.rows.map((r) => (
          <div
            key={r.browser}
            className="flex items-center gap-3 text-xs"
            data-testid={`vdash-browser-${r.browser}`}
          >
            <span className="text-neutral-300 w-44 shrink-0 truncate">
              {BROWSER_LABELS[r.browser] || r.browser}
            </span>
            <span className="text-neutral-100 w-10 text-right tabular-nums" title="uploads">
              {r.uploads}
            </span>
            <span className="text-emerald-300 w-12 text-right tabular-nums" title="success rate">
              {r.success_rate_pct}%
            </span>
            <span className="text-red-300/80 w-10 text-right tabular-nums" title="rejected">
              {r.rejected}
            </span>
            <span className="text-neutral-600 w-12 text-right tabular-nums" title="share of rejections">
              {r.rejection_share_pct}%
            </span>
            <div className="flex-1">
              <Bar value={r.uploads} max={data.total_uploads} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-neutral-600 mt-3">
        columns: uploads · success rate · rejected · share of rejections
      </p>
    </div>
  );
}

function PostNormalization({ data }) {
  // Iter 38 — Post-Normalization slice. Filtered to uploads AFTER the
  // Video Normalization Layer (Iter 37) first emitted an event.
  const FAILURE_BUCKET_LABELS = {
    gemini_rejected:            "Gemini rejected",
    multi_player_no_id:         "Multi-player (no role chip)",
    non_cricket:                "Non-cricket content",
    unsupported_format:         "Unsupported format / codec",
    processing_timeout:         "Processing timeout",
    recovery_cached_file_missing: "Recovery cached file missing",
    unknown_internal:           "Unknown internal",
  };
  if (!data) return null;

  // Normalization not yet deployed in this environment
  if (!data.active) {
    return (
      <div
        className="bento-card p-6"
        data-testid="vdash-post-normalization"
      >
        <p className="label-eyebrow mb-2">post-normalization view</p>
        <div
          className="rounded border border-amber-900/50 bg-amber-950/20 p-4"
          data-testid="vdash-post-normalization-inactive"
        >
          <p className="text-sm text-amber-300">
            Video Normalization Layer has not fired any events in this environment yet.
          </p>
          <p className="text-xs text-amber-200/60 mt-1">
            Either the latest deploy is not live, or no uploads have hit the
            normalize path since deploy. If you&apos;re checking production right
            after a deploy, wait for the next upload.
          </p>
        </div>
      </div>
    );
  }

  const total = data.total_uploads || 0;
  const success = data.successful_reviews || 0;
  const rejected = data.rejected_reviews || 0;
  const rate = data.success_rate_pct ?? 0;
  const deployDate = fmtIso(data.deploy_at);
  const windowDate = fmtIso(data.window_start);

  return (
    <div className="bento-card p-6" data-testid="vdash-post-normalization">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <p className="label-eyebrow">post-normalization view</p>
        <p
          className="text-[10px] uppercase tracking-widest text-neutral-600"
          data-testid="vdash-post-normalization-window"
        >
          since {windowDate} · deploy {deployDate}
        </p>
      </div>

      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-px mb-4"
        style={{ background: "var(--border-default)" }}
      >
        <Stat label="uploads" value={total} />
        <Stat label="successful" value={success} accent />
        <Stat label="rejected" value={rejected} />
        <Stat
          label="success rate"
          value={`${rate}%`}
          accent={rate >= 60}
        />
      </div>

      <p className="label-eyebrow mb-2">normalization events (same window)</p>
      <div
        className="grid grid-cols-3 gap-px mb-4"
        style={{ background: "var(--border-default)" }}
      >
        <Stat label="normalized" value={data.normalized} accent />
        <Stat label="skipped" value={data.skipped} />
        <Stat label="failed normalize" value={data.failed_normalization} />
      </div>

      <p className="label-eyebrow mb-2">failure breakdown</p>
      {rejected === 0 ? (
        <p
          className="text-xs text-neutral-500 italic py-2"
          data-testid="vdash-post-normalization-no-failures"
        >
          No failed uploads in this window.
        </p>
      ) : (
        <div
          className="space-y-1.5"
          data-testid="vdash-post-normalization-breakdown"
        >
          {(data.failure_breakdown || []).map((b) => (
            <div
              key={b.bucket}
              className="flex items-center gap-3 text-xs"
              data-testid={`vdash-breakdown-${b.bucket}`}
            >
              <span className="text-neutral-300 w-56 shrink-0">
                {FAILURE_BUCKET_LABELS[b.bucket] || b.bucket}
              </span>
              <span className="text-neutral-100 w-10 text-right tabular-nums">
                {b.count}
              </span>
              <span className="text-neutral-600 w-12 text-right tabular-nums">
                {b.pct}%
              </span>
              <div className="flex-1">
                <Bar value={b.count} max={rejected} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueSignals({ data }) {
  if (!data) return null;
  return (
    <Section title="value signals" testid="vdash-value-signals">
      <p className="text-xs text-neutral-500 mb-4 italic">
        Strong retention indicators. The higher these are, the more value users are getting.
      </p>
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-px"
        style={{ background: "var(--border-default)" }}
      >
        <Stat label="2nd video" value={data.uploaded_second_video} accent />
        <Stat label="3+ videos" value={data.uploaded_three_plus} accent />
        <Stat label="again ≤7d" value={data.uploaded_again_within_7_days} accent />
        <Stat label="asked follow-up" value={data.asked_followups_after_review} accent />
      </div>
    </Section>
  );
}

function Leaderboard({ rows }) {
  if (!rows) return null;
  return (
    <Section title="most active uploaders" testid="vdash-leaderboard" count={rows.length}>
      {rows.length === 0 ? (
        <p className="text-neutral-500 text-sm italic py-2">
          No video uploads in this window.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="vdash-leaderboard-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
                <th className="text-left font-normal py-2 px-2">player</th>
                <th className="text-right font-normal py-2 px-2">uploads</th>
                <th className="text-right font-normal py-2 px-2">success</th>
                <th className="text-right font-normal py-2 px-2">reports</th>
                <th className="text-right font-normal py-2 px-2">follow-ups</th>
                <th className="text-right font-normal py-2 px-2">last</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className="border-t" style={{ borderColor: "var(--border-default)" }}>
                  <td className="py-2 px-2">
                    <div className="text-neutral-200">{r.name || "—"}</div>
                    <div className="text-[11px] text-neutral-600">
                      {r.email || r.phone_number || r.user_id?.slice(0, 12)}
                    </div>
                  </td>
                  <td className="text-right py-2 px-2 text-neutral-200">{r.uploads}</td>
                  <td className="text-right py-2 px-2 text-neutral-300">{r.successful_reviews}</td>
                  <td className="text-right py-2 px-2 text-neutral-400">{r.reflection_reports}</td>
                  <td className="text-right py-2 px-2 text-neutral-400">{r.follow_up_messages}</td>
                  <td className="text-right py-2 px-2 text-[11px] text-neutral-600">
                    {fmtIso(r.last_video)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Feedback({ data }) {
  if (!data) return null;
  const total = data.total_responses || 0;
  const nps = data.nps_normalised ?? 0;
  const npsSum = data.nps_sum ?? 0;
  const npsColor =
    nps >= 50 ? "var(--highlight)" : nps >= 0 ? "#e8e8e8" : "#d97a7a";

  return (
    <Section
      title="video review feedback"
      testid="vdash-feedback"
      count={total}
    >
      {total === 0 ? (
        <p className="text-neutral-500 text-sm italic py-2">
          No feedback responses yet. Each successful review prompts a 👍 / 👎
          reply, then a one-tap follow-up to learn what helped / what was missing.
        </p>
      ) : (
        <>
          {/* Top row: NPS hero + helpful/not_helpful */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-px mb-5"
            style={{ background: "var(--border-default)" }}
          >
            <div
              className="surface-1 p-5"
              data-testid="vdash-feedback-nps"
              style={{ borderLeft: `2px solid ${npsColor}` }}
            >
              <p className="label-eyebrow mb-1">video review nps</p>
              <p
                className="font-display text-2xl font-light leading-none"
                style={{ color: npsColor }}
              >
                {nps > 0 ? `+${nps}` : nps}
              </p>
              <p className="text-[10px] text-neutral-600 mt-2">
                raw {npsSum > 0 ? `+${npsSum}` : npsSum}
              </p>
            </div>
            <Stat label="👍 helpful" value={`${data.helpful_pct}%`} accent />
            <Stat label="👎 not helpful" value={`${data.not_helpful_pct}%`} />
            <Stat label="total responses" value={total} />
          </div>

          {/* Helpful% bar */}
          <div
            className="w-full h-2 rounded-full overflow-hidden mb-6"
            style={{ background: "var(--surface-2)" }}
          >
            <div
              className="h-full"
              style={{
                width: `${data.helpful_pct}%`,
                background: "var(--highlight)",
              }}
            />
          </div>

          {/* Component breakdowns: what helped / what was missing */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div data-testid="vdash-helpful-breakdown">
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">
                most helpful component
              </p>
              {(data.helpful_breakdown || []).length === 0 ? (
                <p className="text-xs text-neutral-500 italic">
                  No follow-up answers yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.helpful_breakdown.map((b) => (
                    <li
                      key={b.key}
                      className="text-sm text-neutral-300 flex items-baseline justify-between"
                    >
                      <span>{b.label}</span>
                      <span className="text-neutral-500 text-xs">×{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div data-testid="vdash-improvement-breakdown">
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">
                most requested improvement
              </p>
              {(data.not_helpful_breakdown || []).length === 0 ? (
                <p className="text-xs text-neutral-500 italic">
                  No improvement requests yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.not_helpful_breakdown.map((b) => (
                    <li
                      key={b.key}
                      className="text-sm text-neutral-300 flex items-baseline justify-between"
                    >
                      <span>{b.label}</span>
                      <span className="text-neutral-500 text-xs">×{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Trend strip — 7d vs 30d */}
          {data.trend_7d && data.trend_30d && (
            <div
              className="grid grid-cols-2 gap-px mb-6"
              style={{ background: "var(--border-default)" }}
              data-testid="vdash-feedback-trends"
            >
              <div className="surface-1 p-4">
                <p className="label-eyebrow mb-1">last 7 days</p>
                <p className="text-sm text-neutral-200">
                  {data.trend_7d.responses} responses
                </p>
                <p className="text-[11px] text-neutral-500 mt-1">
                  {data.trend_7d.helpful_pct}% helpful · nps{" "}
                  {data.trend_7d.nps_normalised > 0
                    ? `+${data.trend_7d.nps_normalised}`
                    : data.trend_7d.nps_normalised}
                </p>
              </div>
              <div className="surface-1 p-4">
                <p className="label-eyebrow mb-1">last 30 days</p>
                <p className="text-sm text-neutral-200">
                  {data.trend_30d.responses} responses
                </p>
                <p className="text-[11px] text-neutral-500 mt-1">
                  {data.trend_30d.helpful_pct}% helpful · nps{" "}
                  {data.trend_30d.nps_normalised > 0
                    ? `+${data.trend_30d.nps_normalised}`
                    : data.trend_30d.nps_normalised}
                </p>
              </div>
            </div>
          )}

          {/* Recent samples */}
          {data.most_helpful?.length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">
                recent — helpful
              </p>
              <ul className="space-y-1.5">
                {data.most_helpful.slice(0, 5).map((r, i) => (
                  <li key={i} className="text-xs text-neutral-400">
                    <span className="text-neutral-200">👍</span> {r.kind || "—"}
                    {r.followup_answer
                      ? ` · ${r.followup_answer.replace(/_/g, " ")}`
                      : ""}
                    <span className="text-neutral-600">
                      {" · "}
                      {fmtIso(r.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.least_helpful?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">
                recent — not helpful
              </p>
              <ul className="space-y-1.5">
                {data.least_helpful.slice(0, 5).map((r, i) => (
                  <li key={i} className="text-xs text-neutral-400">
                    <span className="text-neutral-200">👎</span> {r.kind || "—"}
                    {r.followup_answer
                      ? ` · ${r.followup_answer.replace(/_/g, " ")}`
                      : ""}
                    <span className="text-neutral-600">
                      {" · "}
                      {fmtIso(r.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function FeatureDemand({ data }) {
  if (!data) return null;
  const buckets = data.buckets || [];
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <Section title="feature demand" testid="vdash-feature-demand" count={buckets.length}>
      <p className="text-xs text-neutral-500 mb-4 italic">
        How often users ask for each feature in their messages. Helps you decide what to build next.
      </p>
      {buckets.length === 0 ? (
        <p className="text-neutral-500 text-sm italic py-2">No keyword hits yet.</p>
      ) : (
        <ul className="space-y-3" data-testid="vdash-feature-demand-list">
          {buckets.map((b) => (
            <li key={b.feature}>
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-sm text-neutral-200">{b.feature.replace(/_/g, " ")}</p>
                <p className="text-xs text-neutral-500">
                  <span className="text-neutral-300">{b.count}</span> mentions ·{" "}
                  <span className="text-neutral-400">{b.unique_users}</span> users
                </p>
              </div>
              <Bar value={b.count} max={max} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function FounderInsights({ data }) {
  if (!data) return null;
  return (
    <Section title="founder insights" testid="vdash-founder-insights">
      <p className="text-xs text-neutral-500 mb-4 italic">
        Auto-extracted from video review payloads + follow-up conversations.
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Themes */}
        <div data-testid="vdash-themes">
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-3">
            most common observation themes
          </p>
          {(data.most_common_themes || []).length === 0 ? (
            <p className="text-xs text-neutral-500 italic">
              Need more reviews before themes emerge.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.most_common_themes.slice(0, 8).map((t, i) => (
                <li key={i} className="text-sm text-neutral-300 flex items-baseline justify-between">
                  <span>{t.theme}</span>
                  <span className="text-neutral-500 text-xs ml-3">
                    {t.user_count} {t.user_count === 1 ? "user" : "users"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Drills */}
        <div data-testid="vdash-drills">
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-3">
            most common drills suggested
          </p>
          {(data.most_common_drills || []).length === 0 ? (
            <p className="text-xs text-neutral-500 italic">No drills yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.most_common_drills.slice(0, 8).map((d, i) => (
                <li key={i} className="text-sm text-neutral-300 flex items-baseline justify-between">
                  <span className="truncate pr-2">{d.drill}</span>
                  <span className="text-neutral-500 text-xs flex-shrink-0">×{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Problems */}
        <div data-testid="vdash-problems">
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-3">
            most common player problems
          </p>
          {(data.most_common_problems || []).length === 0 ? (
            <p className="text-xs text-neutral-500 italic">No recurring problems surfaced.</p>
          ) : (
            <ul className="space-y-2">
              {data.most_common_problems.slice(0, 8).map((p, i) => (
                <li key={i} className="text-sm text-neutral-300 flex items-baseline justify-between">
                  <span>{p.theme}</span>
                  <span className="text-neutral-500 text-xs">
                    {p.user_count} {p.user_count === 1 ? "user" : "users"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Follow-up questions */}
        <div data-testid="vdash-questions">
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 mb-3">
            most common follow-up questions
          </p>
          {(data.most_common_follow_up_questions || []).length === 0 ? (
            <p className="text-xs text-neutral-500 italic">No follow-up questions yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.most_common_follow_up_questions.slice(0, 6).map((q, i) => (
                <li key={i} className="text-xs text-neutral-400">
                  <span className="text-neutral-200">×{q.count}</span> · &ldquo;{q.question}&rdquo;
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}

function ProspectsTable({ rows, testid }) {
  if (!rows?.length) {
    return (
      <p className="text-neutral-500 text-sm italic py-2">
        No users in this bucket.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
            <th className="text-left font-normal py-2 px-2">player</th>
            <th className="text-right font-normal py-2 px-2">uploads</th>
            <th className="text-right font-normal py-2 px-2">successful</th>
            <th className="text-right font-normal py-2 px-2">days</th>
            <th className="text-right font-normal py-2 px-2">refs</th>
            <th className="text-center font-normal py-2 px-2">amb</th>
            <th className="text-center font-normal py-2 px-2">gate</th>
            <th className="text-center font-normal py-2 px-2">priced</th>
            <th className="text-right font-normal py-2 px-2">last</th>
            <th
              className="text-right font-normal py-2 px-2"
              style={{ color: "var(--highlight)" }}
            >
              score
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr
              key={p.user_id}
              className="border-t"
              style={{ borderColor: "var(--border-default)" }}
            >
              <td className="py-2 px-2">
                <div className="text-neutral-200">{p.name || "—"}</div>
                <div className="text-[11px] text-neutral-600">
                  {p.email || p.phone_number || p.user_id?.slice(0, 12)}
                </div>
              </td>
              <td className="text-right py-2 px-2 text-neutral-200">
                {p.uploads}
              </td>
              <td className="text-right py-2 px-2 text-neutral-300">
                {p.successful_reviews}
              </td>
              <td className="text-right py-2 px-2 text-neutral-400">
                {p.days_active}
              </td>
              <td className="text-right py-2 px-2 text-neutral-400">
                {p.referrals}
              </td>
              <td className="text-center py-2 px-2">
                {p.is_ambassador ? (
                  <span title={p.referral_code} className="text-neutral-200">
                    🎖
                  </span>
                ) : (
                  <span className="text-neutral-700">—</span>
                )}
              </td>
              <td className="text-center py-2 px-2">
                {p.hit_paywall ? (
                  <span className="text-neutral-100">●</span>
                ) : (
                  <span className="text-neutral-700">○</span>
                )}
              </td>
              <td className="text-center py-2 px-2">
                {p.pricing_interest ? (
                  <span style={{ color: "var(--highlight)" }}>●</span>
                ) : (
                  <span className="text-neutral-700">○</span>
                )}
              </td>
              <td className="text-right py-2 px-2 text-[11px] text-neutral-600">
                {p.last_activity ? p.last_activity.slice(0, 10) : "—"}
              </td>
              <td
                className="text-right py-2 px-2 font-display"
                style={{ color: "var(--highlight)" }}
              >
                {p.plus_score}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlusProspects({ data }) {
  const TABS = [
    { key: "at_cap_users", label: "at cap (ready today)", testid: "vdash-tab-at-cap" },
    { key: "near_cap_users", label: "near cap", testid: "vdash-tab-near-cap" },
    { key: "hit_paywall_users", label: "hit paywall", testid: "vdash-tab-hit-paywall" },
    { key: "pricing_interest_users", label: "pricing intent", testid: "vdash-tab-pricing" },
    { key: "most_valuable_users", label: "most valuable", testid: "vdash-tab-most-valuable" },
  ];
  const [active, setActive] = useState("at_cap_users");
  if (!data) return null;

  const ready = data.ready_for_plus_count ?? 0;
  const trend = data.ready_for_plus_trend_7d;
  const trendStr =
    trend == null
      ? ""
      : trend > 0
      ? `▲ +${trend}`
      : trend < 0
      ? `▼ ${trend}`
      : "no change";
  const activeRows = data[active] || [];
  const freeLimit = data.free_limit ?? 3;

  return (
    <Section
      title="potential gully plus users"
      testid="vdash-plus-prospects"
    >
      {/* Hero */}
      <div className="mb-6" data-testid="vdash-plus-hero">
        <p className="label-eyebrow mb-2">ready for gully plus</p>
        <div className="flex items-baseline gap-4 flex-wrap">
          <p
            className="font-display font-light leading-none"
            style={{ color: "var(--highlight)", fontSize: "3rem" }}
            data-testid="vdash-plus-hero-count"
          >
            {ready}
          </p>
          <p className="text-xs text-neutral-500" data-testid="vdash-plus-hero-trend">
            {trendStr ? <>{trendStr} vs 7 days ago</> : null}
          </p>
        </div>
        <p className="text-xs text-neutral-600 mt-2 italic">
          Users with ≥ {freeLimit} successful reviews. They&rsquo;ve cleared the free
          tier and are realistic Plus candidates today.
        </p>
      </div>

      {/* Legacy stat strip — kept for at-a-glance */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-px mb-6"
        style={{ background: "var(--border-default)" }}
      >
        <Stat label="at cap" value={(data.at_cap_users || []).length} />
        <Stat label="near cap" value={(data.near_cap_users || []).length} />
        <Stat label="hit paywall" value={data.hit_free_limit_count} />
        <Stat
          label="pricing intent"
          value={data.asked_for_more_or_priced_count}
          accent
        />
      </div>

      {/* Tabs */}
      <div
        className="flex flex-wrap gap-1.5 mb-4 text-xs"
        data-testid="vdash-plus-tabs"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => setActive(t.key)}
            className={`px-3 py-1 rounded ${
              active === t.key
                ? "bg-white/10 text-neutral-100"
                : "text-neutral-500 hover:text-neutral-300"
            }`}
          >
            {t.label}
            <span className="ml-1.5 text-[10px] text-neutral-600">
              {(data[t.key] || []).length}
            </span>
          </button>
        ))}
      </div>

      {/* Plus Score legend (tiny, on demand) */}
      <details className="mb-3 text-[11px] text-neutral-500">
        <summary className="cursor-pointer hover:text-neutral-300">
          Plus Score weighting
        </summary>
        <ul className="mt-2 ml-4 space-y-0.5">
          <li>+1 per upload</li>
          <li>+3 per additional active day (cap +30)</li>
          <li>+5 reached cap (≥ {freeLimit} successful)</li>
          <li>+10 hit paywall</li>
          <li>+15 pricing intent</li>
          <li>+25 referred another player (verified join)</li>
        </ul>
      </details>

      <ProspectsTable
        rows={activeRows}
        testid={`vdash-prospects-table-${active}`}
      />
    </Section>
  );
}

// --- Iter 39: Debug Snapshot (admin-only, read-only) ---
// Single panel that pulls the four diagnostic endpoints in parallel so the
// founder can answer 'is the dashboard stale / is normalization active /
// where's the paywall drop-off' without DevTools or curl.
const SNAPSHOT_ENDPOINTS = [
  { key: "ffmpeg_health",        url: "/admin/health/ffmpeg",                       label: "ffmpeg health (path / version / source / available)" },
  { key: "video_dashboard",      url: "/admin/video-dashboard?since_days=30",       label: "video-dashboard (30d, includes post_normalization + ffmpeg_health)" },
  { key: "normalization_impact", url: "/admin/web/normalization-impact?hours=168",  label: "normalization-impact (7d, success rate by input codec/container)" },
  { key: "recovery_breakdown",   url: "/admin/web/recovery-breakdown?hours=168",    label: "recovery-breakdown (7d, multi-player recovery stages)" },
  { key: "multi_player_audit",   url: "/admin/web/multi-player-audit?hours=168",    label: "multi-player audit (7d, what Gemini saw on each rejection + recoverability)" },
  { key: "founder_dashboard",    url: "/admin/web/founder-dashboard",                label: "founder-dashboard (funnel + paywall + checkout + payments)" },
];

function DebugSnapshot() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchSnapshot = async () => {
    setLoading(true);
    const results = await Promise.all(
      SNAPSHOT_ENDPOINTS.map(async (e) => {
        try {
          const { data } = await api.get(e.url);
          return [e.key, { ok: true, status: 200, data }];
        } catch (err) {
          return [
            e.key,
            {
              ok: false,
              status: err?.response?.status || 0,
              error:
                err?.response?.data?.detail ||
                err?.message ||
                "request failed",
            },
          ];
        }
      })
    );
    const payload = {
      generated_at: new Date().toISOString(),
      ...Object.fromEntries(results),
    };
    setSnap(payload);
    setLoading(false);
  };

  const copy = async () => {
    if (!snap) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(snap, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard refused — fall back to a textarea selection
      const ta = document.createElement("textarea");
      ta.value = JSON.stringify(snap, null, 2);
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_e) { /* clipboard refused even in fallback */ }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="bento-card p-6" data-testid="vdash-debug-snapshot">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <p className="label-eyebrow">debug snapshot · admin-only · read-only</p>
        <div className="flex items-center gap-2 text-xs">
          <button
            data-testid="vdash-debug-fetch"
            onClick={() => { setOpen(true); fetchSnapshot(); }}
            disabled={loading}
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-300 hover:text-neutral-100 hover:border-neutral-500 disabled:opacity-50"
          >
            {loading ? "Fetching…" : snap ? "Refresh snapshot" : "Run snapshot"}
          </button>
          <button
            data-testid="vdash-debug-copy"
            onClick={copy}
            disabled={!snap}
            className="px-3 py-1 rounded border border-neutral-700 text-neutral-300 hover:text-neutral-100 hover:border-neutral-500 disabled:opacity-30"
          >
            {copied ? "✓ Copied" : "Copy diagnostic JSON"}
          </button>
        </div>
      </div>

      <p className="text-xs text-neutral-500 italic mb-3">
        Pulls /admin/video-dashboard · /admin/web/normalization-impact ·
        /admin/web/recovery-breakdown · /admin/web/founder-dashboard in parallel.
        Use the &ldquo;Copy diagnostic JSON&rdquo; button to paste the full snapshot to me for analysis.
      </p>

      {!snap && !loading && (
        <p className="text-xs text-neutral-600" data-testid="vdash-debug-idle">
          Click &ldquo;Run snapshot&rdquo; to fetch all four diagnostic endpoints.
        </p>
      )}

      {snap && (
        <div className="space-y-3" data-testid="vdash-debug-payload">
          <p className="text-[10px] text-neutral-600 tabular-nums">
            snapshot · {fmtIso(snap.generated_at)} {snap.generated_at?.slice(11, 19)}
          </p>
          {SNAPSHOT_ENDPOINTS.map((e) => {
            const blob = snap[e.key] || {};
            const isError = blob.ok === false;
            return (
              <details
                key={e.key}
                open={open && !isError ? false : isError}
                className="rounded border border-neutral-800"
                data-testid={`vdash-debug-${e.key}`}
              >
                <summary className="px-3 py-2 cursor-pointer text-xs text-neutral-300 hover:bg-white/5 flex items-center justify-between gap-2">
                  <span>
                    <span className="text-neutral-500 mr-2">{e.url}</span>
                    <span className="text-neutral-400">{e.label}</span>
                  </span>
                  <span
                    className={`text-[10px] tabular-nums ${
                      isError ? "text-red-400" : "text-emerald-400"
                    }`}
                  >
                    {isError ? `✗ ${blob.status || "ERR"}` : "✓ 200"}
                  </span>
                </summary>
                <pre
                  className="px-3 py-2 text-[10px] leading-relaxed text-neutral-400 overflow-auto max-h-96 bg-black/30"
                  data-testid={`vdash-debug-${e.key}-pre`}
                >
                  {JSON.stringify(isError ? blob : blob.data, null, 2)}
                </pre>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --- Container ---
export default function VideoDashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sinceDays, setSinceDays] = useState(30);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Iter 39: silent background refresh every 60s — no spinner flash, and a
    // failed background poll never clears existing data on the screen.
    const run = async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      if (!silent) setError(null);
      try {
        const { data: payload } = await api.get(
          `/admin/video-dashboard?since_days=${sinceDays}`
        );
        if (!cancelled) {
          setData(payload);
          setLastFetchedAt(new Date());
          if (!silent) setError(null);
        }
      } catch (e) {
        if (!cancelled && !silent) {
          setError(e?.response?.data?.detail || "Couldn't load video dashboard");
          setData(null);
        }
        // silent failures: keep showing whatever we have, fail silently
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    run();
    const t = setInterval(() => run({ silent: true }), 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [sinceDays, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  const lastFetchedLabel = lastFetchedAt
    ? `updated ${lastFetchedAt.toTimeString().slice(0, 8)}`
    : null;

  return (
    <div className="mb-12" data-testid="video-dashboard-section">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <p className="label-eyebrow">video review dashboard</p>
          {lastFetchedLabel && (
            <p
              className="text-[10px] text-neutral-600 tabular-nums"
              data-testid="vdash-last-fetched"
            >
              {lastFetchedLabel} · auto-refresh 60s
            </p>
          )}
        </div>
        <div className="flex gap-1.5 text-xs" data-testid="vdash-window-pills">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`vdash-window-${d}`}
              onClick={() => setSinceDays(d)}
              className={`px-3 py-1 rounded ${
                sinceDays === d ? "bg-white/10 text-neutral-100" : "text-neutral-500 hover:text-neutral-300"
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            data-testid="vdash-refresh"
            onClick={refresh}
            className="px-3 py-1 rounded text-neutral-500 hover:text-neutral-300"
          >
            ↻
          </button>
        </div>
      </div>

      {loading && (
        <p className="text-neutral-600 text-xs py-4" data-testid="vdash-loading">
          Loading…
        </p>
      )}

      {error && (
        <p className="text-neutral-500 text-sm italic py-4" data-testid="vdash-error">
          {error}
        </p>
      )}

      {!loading && !error && data && (
        <div className="space-y-6" data-testid="vdash-content">
          <FfmpegHealthBanner data={data.ffmpeg_health} />
          <DebugSnapshot />
          <Overview data={data.overview} />
          <BrowserBreakdown data={data.browser_breakdown} />
          <PostNormalization data={data.post_normalization} />
          <ValueSignals data={data.value_signals} />
          <Leaderboard rows={data.leaderboard || []} />
          <Feedback data={data.feedback} />
          <FeatureDemand data={data.feature_demand} />
          <FounderInsights data={data.founder_insights} />
          <PlusProspects data={data.plus_prospects} />
          <p className="text-[10px] text-neutral-700 italic" data-testid="vdash-generated-at">
            generated · {fmtIso(data.generated_at)}
          </p>
        </div>
      )}
    </div>
  );
}
